/**
 * The protocol console.
 *
 * THE SECOND OF DEEP MODE'S TWO CONTAINERS. `settings.md` §4 says every geek
 * surface lives in the Inspector drawer or in here, so that "deep off is a
 * complete removal, not a scattering of leftovers" — and this component renders
 * nothing at all unless the origin says the console is available and this
 * principal may read it.
 *
 * IT ENFORCES NOTHING. Every refusal in here is a repetition of one the origin
 * makes: the verb list comes from the server, the arming window is the server's,
 * and a command this component was happy with is still classified again before
 * it reaches a socket. What the client adds is the GESTURE — hold to arm, a
 * countdown, disarm on blur — which is ergonomics and not security, and is
 * marked as such wherever it appears.
 *
 * THE TRANSCRIPT IS THE PRODUCT. A console that summarised what happened would
 * be a worse version of the app around it; the only reason to type IMAP by hand
 * is to see exactly what the server said, byte for byte, with the timing.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import { api, type ConsoleExchange, type ConsoleStatus, type ConsoleTransport } from './api.ts'

/** geek.md's press. Long enough to be deliberate, short enough not to be a chore. */
const HOLD_MS = 800
/** geek.md's blur rule. A client courtesy: the origin's window is five minutes. */
const BLUR_DISARM_MS = 60_000

/**
 * How each transport is spelled on its tab. NOT WHICH TABS THERE ARE.
 *
 * This was the list, and a list here is a claim about the server. It offered
 * ManageSieve and SMTP on every build, including the ones with no
 * `MANAGESIEVE_HOST` and no `SUBMISSION_HOST` — so two of the three tabs sent
 * their commands at a connection that could not answer them and showed the
 * timeout as a result. WHICH TRANSPORTS EXIST IS THE ORIGIN'S ANSWER: it
 * advertises a verb set per transport it can actually open, and the keys of that
 * are the tabs. A build that gains a fourth needs a word added here and nothing
 * else.
 */
const LABELS: Readonly<Record<ConsoleTransport, string>> = {
  imap: 'IMAP',
  managesieve: 'ManageSieve',
  smtp: 'SMTP',
}

export interface ConsoleProps {
  readonly open: boolean
  readonly onClose: () => void
}

interface Entry extends ConsoleExchange {
  readonly at: number
}

export function Console({ open, onClose }: ConsoleProps): React.JSX.Element | null {
  const [status, setStatus] = useState<ConsoleStatus | null>(null)
  const [transport, setTransport] = useState<ConsoleTransport>('imap')
  const [line, setLine] = useState('')
  const [entries, setEntries] = useState<readonly Entry[]>([])
  const [error, setError] = useState<string | null>(null)
  const [armedUntil, setArmedUntil] = useState<number | null>(null)
  const [holding, setHolding] = useState(false)
  const [now, setNow] = useState(() => Date.now())
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const tailRef = useRef<HTMLDivElement>(null)

  /**
   * The transports this origin will actually open, in its order.
   *
   * `LABELS` IS CONSULTED AS A FILTER AND NOT AS THE SOURCE: an origin newer
   * than this bundle could name a transport this build has no word for, and a
   * tab labelled `undefined` is worse than a tab that is not there.
   */
  const offered = (Object.keys(status?.verbs ?? {}) as ConsoleTransport[]).filter((t) => Object.hasOwn(LABELS, t))
  // WHAT IS SELECTED HAS TO BE SOMETHING THAT EXISTS. The state starts at `imap`
  // — the one transport every build has — and falls back rather than sending a
  // line at a transport the origin just said it has no connection for.
  const active: ConsoleTransport | null = offered.includes(transport) ? transport : (offered[0] ?? null)

  useEffect(() => {
    if (!open) return
    api
      .consoleStatus()
      .then((s) => {
        setStatus(s)
        setArmedUntil(s.armedFor > 0 ? Date.now() + s.armedFor : null)
      })
      // SILENT, AND THE SURFACE STAYS CLOSED. A console that could not ask
      // whether it exists renders nothing rather than a box that refuses
      // everything typed into it.
      .catch(() => setStatus(null))
  }, [open])

  // ONE TICK A SECOND, ONLY WHILE ARMED. The countdown is the whole reason
  // somebody trusts that the window is real, and a timer that ran always would
  // re-render a transcript nobody is looking at.
  useEffect(() => {
    if (!open || armedUntil === null) return
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [open, armedUntil])

  useEffect(() => {
    if (armedUntil !== null && now >= armedUntil) setArmedUntil(null)
  }, [now, armedUntil])

  // DISARM ON BLUR, AFTER A MINUTE. A courtesy and not a control: the origin's
  // window runs for five minutes whatever this does, which is why the panel says
  // so rather than implying the browser is enforcing anything.
  useEffect(() => {
    if (!open || armedUntil === null) return
    let timer: ReturnType<typeof setTimeout> | null = null
    const onVisibility = (): void => {
      if (document.hidden) {
        timer = setTimeout(() => {
          void api.disarm().catch(() => undefined)
          setArmedUntil(null)
        }, BLUR_DISARM_MS)
      } else if (timer) {
        clearTimeout(timer)
        timer = null
      }
    }
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      if (timer) clearTimeout(timer)
    }
  }, [open, armedUntil])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    // `?.scrollIntoView?.(` — THE METHOD IS GUARDED AND NOT JUST THE REF, and
    // the difference is a crash. `scrollIntoView` is not universally
    // implemented; jsdom has no layout and does not define it, and neither do
    // some embedded webviews. The optional chain on `tailRef.current` guards a
    // ref that is null, which is not the case that happens here — the element
    // exists and the method does not, so the call threw and React tore the whole
    // console down on the first command anybody sent in such an environment.
    //
    // Scrolling to the newest line is a convenience. Losing the transcript is
    // not a trade worth making for it.
    tailRef.current?.scrollIntoView?.({ block: 'end' })
  }, [entries])

  const submit = useCallback(() => {
    const text = line.trim()
    if (text === '' || active === null) return
    setError(null)
    api
      .consoleSend(active, text)
      .then((exchange) => {
        setEntries((current) => [...current, { ...exchange, at: Date.now() }])
        setLine('')
      })
      .catch((failure: unknown) => {
        // THE REFUSAL IS SHOWN IN THE TRANSCRIPT, not in a toast. A console's
        // history is the record of what was attempted, and a refusal that
        // vanished on the next keystroke would leave a gap in it.
        const message = failure instanceof Error ? failure.message : 'That did not go through'
        setEntries((current) => [
          ...current,
          { tag: '', class: 'read', verb: '', sent: text, received: `!! ${message}`, ms: 0, bytes: 0, truncated: false, at: Date.now() },
        ])
        setError(message)
      })
  }, [line, active])

  const beginHold = useCallback(() => {
    if (!status?.canArm || armedUntil !== null) return
    setHolding(true)
    holdTimer.current = setTimeout(() => {
      setHolding(false)
      const reason = window.prompt('What are you about to do? It goes in the log beside the command.')
      if (!reason || reason.trim() === '') return
      api
        .arm(reason.trim())
        .then((r) => setArmedUntil(Date.now() + r.remainingMs))
        .catch((failure: unknown) => setError(failure instanceof Error ? failure.message : 'Could not arm'))
    }, HOLD_MS)
  }, [status, armedUntil])

  const endHold = useCallback(() => {
    setHolding(false)
    if (holdTimer.current) clearTimeout(holdTimer.current)
    holdTimer.current = null
  }, [])

  // A CONSOLE WITH NO TRANSPORT IS NOT A CONSOLE. `available` says the origin
  // serves the surface; `offered` says it has somewhere to send a line. Both, or
  // this renders nothing at all — which is the same rule deep mode follows.
  if (!open || !status?.available || active === null) return null

  const secondsLeft = armedUntil === null ? 0 : Math.max(0, Math.ceil((armedUntil - now) / 1000))
  const verbs = status.verbs[active]

  return (
    <div className="console" role="region" aria-label="Protocol console">
      <div className="console-head">
        <div className="console-tabs" role="tablist" aria-label="Transport">
          {offered.map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={active === t}
              className="console-tab"
              onClick={() => setTransport(t)}
            >
              {LABELS[t]}
            </button>
          ))}
        </div>

        {/* THE ARM CONTROL SAYS WHAT IT IS. "Armed" with a countdown is a state
            somebody can check; a button that silently toggles a mode is one they
            have to remember. */}
        {status.canArm ? (
          armedUntil !== null ? (
            <button
              className="console-armed"
              onClick={() => {
                void api.disarm().catch(() => undefined)
                setArmedUntil(null)
              }}
            >
              Armed — {secondsLeft}s · disarm
            </button>
          ) : (
            <button
              className="console-arm"
              data-holding={holding}
              onMouseDown={beginHold}
              onMouseUp={endHold}
              onMouseLeave={endHold}
              onTouchStart={beginHold}
              onTouchEnd={endHold}
            >
              {holding ? 'Keep holding…' : 'Hold to arm'}
            </button>
          )
        ) : (
          // SAID RATHER THAN GREYED OUT. `geek.console.write` is tailnet-only, so
          // the same person sees this on the public path and not at home — and a
          // disabled control with no explanation reads as a bug.
          <span className="console-note">Read-only here — writing needs the tailnet.</span>
        )}

        <button className="console-close" onClick={onClose} aria-label="Close console">
          ×
        </button>
      </div>

      <div className="console-transcript" role="log">
        {entries.length === 0 ? (
          <p className="console-empty">
            {verbs.read.slice(0, 8).join('  ')}
            {verbs.armed.length > 0 ? `   ·  armed: ${verbs.armed.slice(0, 6).join('  ')}` : ''}
          </p>
        ) : (
          entries.map((entry, index) => (
            <div className="console-entry" key={`${entry.at}-${index}`} data-class={entry.class}>
              <div className="console-sent">C: {entry.sent}</div>
              <pre className="console-received">{entry.received}</pre>
              <div className="console-meta">
                {entry.ms} ms · {entry.bytes} bytes
                {entry.truncated ? ' · cut short' : ''}
              </div>
            </div>
          ))
        )}
        <div ref={tailRef} />
      </div>

      <form
        className="console-prompt"
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        {/* NO TAG IN THE BOX. The origin numbers the commands; showing a field
            for it would invite a client to choose one, and a reused tag ends the
            next command's answer early. */}
        <span className="console-caret" aria-hidden="true">
          {active === 'imap' ? 'H····' : '>'}
        </span>
        <input
          ref={inputRef}
          className="console-input"
          value={line}
          spellCheck={false}
          autoComplete="off"
          aria-label={`${active} command`}
          placeholder={active === 'imap' ? 'SELECT INBOX' : active === 'smtp' ? 'EHLO hearth' : 'LISTSCRIPTS'}
          onChange={(event) => setLine(event.target.value)}
        />
        <button type="submit" className="console-send">
          Send
        </button>
      </form>

      {error ? (
        <p className="console-error" role="status">
          {error}
        </p>
      ) : null}
    </div>
  )
}
